import { useEffect, useRef, useState } from "react";

import { Nullable } from "@simplyk/common";
import { useRouter } from "next/router";

export interface UseStepNavigationOptions<TStepKey extends string> {
  steps: { key: TStepKey; shouldBeSkip?: boolean }[];
  /**
   * The last saved draft step key to restore when returning to the editor.
   * Should be calculated by the caller (e.g., only when status is "draft").
   */
  lastSavedDraftStep?: Nullable<string>;
  /**
   * Function to validate if a string is a valid step key.
   * This allows each feature (forms, org pages) to provide their own validation logic.
   */
  isValidStepKey: (key: unknown) => key is TStepKey;
  /**
   * Fallback step key to use when no valid step is found.
   * @default first available step
   */
  fallbackStepKey?: TStepKey;
}

export const useStepNavigation = <TStepKey extends string>({
  steps,
  lastSavedDraftStep,
  isValidStepKey,
  fallbackStepKey,
}: UseStepNavigationOptions<TStepKey>) => {
  const router = useRouter();
  const initialStepKey = getInitialStepKey(lastSavedDraftStep, steps, isValidStepKey, fallbackStepKey);
  const [currentStepKey, setCurrentStepKey] = useState<TStepKey>(initialStepKey);

  const liveEditorStepsKeys = steps.filter((step) => !step.shouldBeSkip).map((step) => step.key);

  const getIndexStepFromKey = (key: string): number => liveEditorStepsKeys.findIndex((step) => step === key);

  const getKeyStepFromIndex = (index: number): TStepKey => liveEditorStepsKeys[index] as TStepKey;

  const currentStepIndex = getIndexStepFromKey(currentStepKey);

  const hasWrittenInitialHashRef = useRef(false);

  useEffect(() => {
    // Handle initial hash and browser back/forward
    const handleHashChange = () => {
      // Use window.location.hash to ensure we get the latest hash
      const hash = window.location.hash.slice(1);
      if (hash && isValidStepKey(hash) && hash !== currentStepKey) {
        setCurrentStepKey(hash);
      }
    };

    if (!window.location.hash) {
      if (!hasWrittenInitialHashRef.current) {
        hasWrittenInitialHashRef.current = true;
        void router.replace({ hash: initialStepKey });
      }
    } else {
      hasWrittenInitialHashRef.current = true;
      handleHashChange();
    }

    router.events.on("hashChangeComplete", handleHashChange);
    window.addEventListener("hashchange", handleHashChange);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
      router.events.off("hashChangeComplete", handleHashChange);
    };
  }, [currentStepKey, initialStepKey, isValidStepKey, router]);

  const updateCurrentStep = (stepKey: TStepKey) => {
    setCurrentStepKey(stepKey);
    window.location.hash = stepKey;
  };

  const goToNextStep = () => {
    const nextStepKey = getKeyStepFromIndex(currentStepIndex + 1);
    updateCurrentStep(nextStepKey);
  };

  const goToPreviousStep = () => {
    const previousStepKey = getKeyStepFromIndex(currentStepIndex - 1);
    updateCurrentStep(previousStepKey);
  };

  return {
    currentStepIndex,
    currentStepKey,
    updateCurrentStep,
    goToNextStep,
    goToPreviousStep,
    getIndexStepFromKey,
    getKeyStepFromIndex,
    liveEditorStepsKeys,
  };
};

const getInitialStepKey = <TStepKey extends string>(
  lastSavedDraftStep: Nullable<string> | undefined,
  steps: { key: TStepKey; shouldBeSkip?: boolean }[],
  isValidStepKey: (key: unknown) => key is TStepKey,
  fallbackStepKey?: TStepKey
): TStepKey => {
  const hash = typeof window !== "undefined" ? window.location.hash.slice(1) : "";
  if (hash && isValidStepKey(hash)) {
    return hash;
  }

  if (lastSavedDraftStep && isValidStepKey(lastSavedDraftStep)) {
    return lastSavedDraftStep;
  }

  // Use fallbackStepKey if provided, otherwise use first available step
  const firstStep = steps.find((step) => !step.shouldBeSkip)?.key;
  return firstStep ?? fallbackStepKey ?? ("" as TStepKey);
};
